import { defineStore } from "pinia";
import { useAuthStore } from "~/stores/authStore";
import { useNuxtApp } from "#app";

// Catégories dynamiques pour achats, contenu, favoris
const PURCHASE_CATEGORIES = [
  "courses",
  "articles",
  "books",
  "stationery",
  "clothing",
  "crafts",
];
const CONTENT_TYPES = [
  "courses",
  "articles",
  "books",
  "stationery",
  "clothing",
  "crafts",
];

export const useUserStore = defineStore("user", {
  state: () => ({
    // Achats
    purchasedCourses: [],
    purchasedArticles: [],
    purchasedBooks: [],
    purchasedStationery: [],
    purchasedClothing: [],
    purchasedCrafts: [],
    userQuizAttempts: [],
    // Contenu créé
    myCourses: [],
    myArticles: [],
    myBooks: [],
    myStationery: [],
    myClothing: [],
    myCrafts: [],
    // Détails utilisateur
    address: null,
    notifications: [],
    favorites: {
      courses: [],
      articles: [],
      books: [],
      stationery: [],
      clothing: [],
      crafts: [],
      profileDetails: null,
    },
    // Factures et progression
    invoices: [],
    progress: [],
    installmentCourses: [],
    subscribedCourses: [],
  }),

  getters: {
    // Nombre total d'achats
    totalPurchases: (state) =>
      PURCHASE_CATEGORIES.reduce(
        (sum, cat) =>
          sum +
          state["purchased" + cat.charAt(0).toUpperCase() + cat.slice(1)]
            .length,
        0
      ),
    // Nombre total de contenus créés
    totalMyContent: (state) =>
      CONTENT_TYPES.reduce(
        (sum, cat) =>
          sum + state["my" + cat.charAt(0).toUpperCase() + cat.slice(1)].length,
        0
      ),
    // Nombre total de favoris
    totalFavorites: (state) =>
      PURCHASE_CATEGORIES.reduce(
        (sum, cat) => sum + state.favorites[cat].length,
        0
      ),
    hasPurchasedCourse: (state) => (slug) =>
      state.purchasedCourses.some((c) => c.slug === slug),
    // ... autres getters existants
    allAccessibleCourses(state) {
      const purchased = state.purchasedCourses || [];
      const installments = state.installmentCourses || [];
      const subscribed = state.subscribedCourses || [];

      // Fusion sans doublon (clé = slug ou product_id)
      const all = [...purchased, ...installments, ...subscribed];
      const unique = [];
      const seen = new Set();
      for (const course of all) {
        const key = course.slug || course.product_id;
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(course);
        }
      }
      return unique;
    },
  
  
    passedQuizzes(state) {
      return state.userQuizAttempts.filter((a) => a.status === "passed");
    },
    getQuizCountForCourse: (state) => (courseId) => {
      return state.userQuizAttempts.filter((a) => a.course_id === courseId)
        .length;
    },
    getLastQuizScore: (state) => (quizId) => {
      const attempts = state.userQuizAttempts.filter(
        (a) => a.quiz_id === quizId
      );
      return attempts.length ? attempts[attempts.length - 1].score : null;
    },
  },

  actions: {
    /**
     * Helper pour assignation profonde (deep assign) des props imbriquées
     */
    setDeep(obj, path, value) {
      const keys = Array.isArray(path) ? path : path.split(".");
      const lastKey = keys.pop();
      const target = keys.reduce((o, k) => o[k], obj);
      if (Array.isArray(target[lastKey])) {
        target[lastKey] = Array.isArray(value) ? value : [];
      } else {
        target[lastKey] = value ?? null;
      }
    },

    /**
     * Helper générique pour fetcher un endpoint et stocker dans state (supporte props imbriquées)
     * @param {string} endpoint ex. "favorites/courses"
     * @param {string|string[]} propPath ex. ['favorites','courses'] ou 'purchasedCourses'
     */

    async fetchCategory(endpoint, propPath) {
      const url = `/api/user/${endpoint.toLowerCase()}`;
      try {
        // 1️⃣ Appel réseau
        const res = await $fetch(url, { credentials: "include" });

        // 2️⃣ Vérification du succès
        if (!res || !res.success) {
          console.error(
            `Erreur fetch ${endpoint} :`,
            res?.message || "Réponse invalide"
          );
          return;
        }

        // 3️⃣ Extraction du tableau à stocker
        const raw =
          res.data ??
          res.cart ??
          res.notifications ??
          res.invoices ??
          res.progress ??
          (Array.isArray(res) ? res : null);

        if (!Array.isArray(raw)) {
          console.error(
            `fetchCategory ${endpoint} n’a pas renvoyé de tableau :`,
            res
          );
          return;
        }

        // 4️⃣ Mise à jour du state via setDeep
        this.setDeep(this, propPath, raw);
        //   console.log("🔗 fetchCategory", url, raw);
      } catch (err) {
        console.error(`Erreur fetchCategory ${endpoint} :`, err);
      }
    },

    // ----- Achats -----
    fetchPurchasedCourses() {
      return this.fetchCategory("purchases/courses", "purchasedCourses");
    },
    fetchPurchasedArticles() {
      return this.fetchCategory("purchases/articles", "purchasedArticles");
    },
    fetchPurchasedBooks() {
      return this.fetchCategory("purchases/books", "purchasedBooks");
    },
    fetchPurchasedStationery() {
      return this.fetchCategory("purchases/stationery", "purchasedStationery");
    },
    fetchPurchasedClothing() {
      return this.fetchCategory("purchases/clothing", "purchasedClothing");
    },
    fetchPurchasedCrafts() {
      return this.fetchCategory("purchases/crafts", "purchasedCrafts");
    },
    fetchAllPurchases() {
      return Promise.all(
        PURCHASE_CATEGORIES.map((cat) =>
          this[`fetchPurchased${cat.charAt(0).toUpperCase() + cat.slice(1)}`]()
        )
      );
    },

    // ----- Contenu -----
    fetchMyCourses() {
      return this.fetchCategory("content/courses", "myCourses");
    },
    fetchMyArticles() {
      return this.fetchCategory("content/articles", "myArticles");
    },
    fetchMyBooks() {
      return this.fetchCategory("content/books", "myBooks");
    },
    fetchMyStationery() {
      return this.fetchCategory("content/stationery", "myStationery");
    },
    fetchMyClothing() {
      return this.fetchCategory("content/clothing", "myClothing");
    },
    fetchMyCrafts() {
      return this.fetchCategory("content/crafts", "myCrafts");
    },
    fetchAllMyContent() {
      return Promise.all(
        CONTENT_TYPES.map((cat) =>
          this[`fetchMy${cat.charAt(0).toUpperCase() + cat.slice(1)}`]()
        )
      );
    },
    async fetchQuizAttempts() {
      try {
        const res = await $fetch("/api/quizzes/get-user-attempts", {
          credentials: "include",
        });
        if (res?.success && Array.isArray(res.data)) {
          this.userQuizAttempts = res.data;
        }
      } catch (err) {
        console.error("Erreur fetchQuizAttempts:", err);
      }
    },

    // ----- Profil & notifications -----

    fetchNotifications() {
      return this.fetchCategory("notifications", "notifications");
    },
    async refreshNotifications() {
      await this.fetchNotifications();
    }, // Marquer une notification comme lue

    // Lire une notification
    async markNotificationAsRead(notificationId) {
      try {
        const response = await $fetch("/api/user/notifications/update-read", {
          method: "PUT",
          body: { notification_id: notificationId },
          credentials: "include",
        });
        //   console.log("✅ Résultat de markNotificationAsRead :", response);
        await this.fetchNotifications(); // ou this.refreshNotifications()
      } catch (error) {
        console.error("Erreur lors du marquage lu :", error);
      }
    },

    // Supprimer une notification
    async removeNotification(notificationId) {
      try {
        const response = await $fetch("/api/user/notifications", {
          method: "PUT",
          body: { notification_id: notificationId },
          credentials: "include",
        });
        //  console.log("✅ Résultat de removeNotification :", response);

        if (response?.success) {
          // Supprimer localement la notification sans attendre un fetch complet
          this.notifications = this.notifications.filter(
            (n) => n.notification_id !== notificationId
          );
        } else {
          console.warn("⚠️ Suppression échouée côté serveur.");
        }
      } catch (error) {
        console.error("Erreur lors de la suppression :", error);
      }
    },

    // Marquer toutes comme lues
    async markAllNotificationsAsRead() {
      try {
        const response = await $fetch("/api/user/notifications/mark-all-read", {
          method: "PUT",
          credentials: "include",
        });
        //   console.log("✅ Résultat de markAllNotificationAsRead :", response);
        await this.fetchNotifications();
      } catch (error) {
        console.error("Erreur lors du marquage global :", error);
      }
    },

    // Supprimer toutes
    async removeAllNotifications() {
      try {
        const response = await $fetch("/api/user/notifications/remove-all", {
          method: "PUT",
          credentials: "include",
        });
        //  console.log("✅ Résultat de removeAllNotifications :", response);
        await this.fetchNotifications();
      } catch (error) {
        console.error("Erreur suppression globale :", error);
      }
    },

    // ----- Favoris -----
    /**
     * Fetch des favoris pour une catégorie
     */
    async fetchFavorites(category) {
      await this.fetchCategory(`favorites/${category}`, [
        "favorites",
        category,
      ]);
    },
    async addFavorite(category, itemId) {
      await $fetch(`/api/user/favorites/${category}`, {
        method: "POST",
        body: { item_id: itemId },
        credentials: "include",
      });
      await this.fetchFavorites(category);
    },
    async removeFavorite(category, itemId) {
      await $fetch(`/api/user/favorites/${category}`, {
        method: "DELETE",
        body: { item_id: itemId },
        credentials: "include",
      });
      await this.fetchFavorites(category);
    },
    // ----- Helpers métiers -----
    /**
     * Vérifie si l’utilisateur est instructeur du cours donné.
     * Ne se lance qu’en client et si l’utilisateur est connecté.
     */
    async isInstructorOnCourse(courseSlug) {
      if (process.server) {
        // on ne fait rien au SSR
        return false;
      }
      if (!courseSlug || typeof courseSlug !== "string") {
        console.warn("isInstructorOnCourse: slug invalide :", courseSlug);
        return false;
      }

      const authStore = useAuthStore();
      if (!authStore.isLoggedIn) {
        // pas connecté → pas instructeur
        return false;
      }

      try {
        const res = await $fetch(
          `/api/products/${encodeURIComponent(courseSlug)}`,
          { credentials: "include" }
        );
        const instructorId = res?.product?.details?.instructor?.id;
        return Boolean(
          instructorId && authStore.profile.user_id === instructorId
        );
      } catch (err) {
        // on loggue mais on renvoie false
        console.info(
          `isInstructorOnCourse (${courseSlug}) → pas instructeur ou erreur`,
          err.status || err.message
        );
        return false;
      }
    },

    /**
     * Même principe pour isStudentOnCourse
     */

    async getStudentAccessDetail(courseSlug) {
      if (process.server) return { hasAccess: false, accessType: null };
      // ... même logique ...
      try {
        const res = await $fetch(
          `/api/user/has-access/${encodeURIComponent(courseSlug)}`,
          { credentials: "include" }
        );
        return {
          hasAccess: res.success === true && Boolean(res.hasAccess),
          accessType: res.accessType || null,
        };
      } catch (err) {
        return { hasAccess: false, accessType: null };
      }
    },
    /*get allAccessibleCourses() {
      // Achats directs
      const purchased = this.purchasedCourses || [];
      // Cours payés en plusieurs fois (à toi de les récupérer avec une API dédiée)
      const installments = this.installmentCourses || [];
      // Abonnements éventuels (à adapter)
      const subscribed = this.subscribedCourses || [];

      // On merge tout en évitant les doublons (par slug ou id)
      const all = [...purchased, ...installments, ...subscribed];
      const unique = [];
      const seen = new Set();

      for (const course of all) {
        const key = course.slug || course.product_id;
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(course);
        }
      }
      return unique;
    },*/
    // userStore.js

    fetchInstallmentCourses() {
      return this.fetchCategory("installments/courses", "installmentCourses");
    },
    fetchSubscribedCourses() {
      return this.fetchCategory("subscriptions/courses", "subscribedCourses");
    },
    async fetchUserProfile() {
      try {
        const data = await $fetch("/api/user/me");
        this.profileDetails = data?.profile || null;
        // if (process.dev) console.log("SHOW PROFILE DETAILS FROM USERSTORE", this.profileDetails)
      } catch (err) {
        this.profileDetails = null;
        if (process.dev) console.error("Erreur fetchUserProfile:", err);
      }
    },
    /*
    isStudentOnCourseSync(slug) {
      // 1. Achat unitaire
      if (this.purchasedCourses?.includes(slug)) return true;
      // 2. Abonnement global
      if (this.hasActiveSubscription && this.subscriptionCoversAllCourses) return true;
      // 3. Abonnement partiel
      if (this.hasActiveSubscription && this.subscriptionCourses?.includes(slug)) return true;
      // 4. Non inscrit
      return false;
    }*/

    /**
     * Vérifie si l'utilisateur a accès à un cours
     * @param {string} courseSlug
     * @param {boolean} returnObject - si true, retourne { hasAccess, accessType }
     * @returns {Promise<boolean|{hasAccess:boolean,accessType:string|null}>}
     */

    isStudentOnCourseSync(slug) {
      return this.allAccessibleCourses?.some(
        (c) => c.slug === slug || c.course_id === slug || c.product_id === slug
      );
    },
    /* async isStudentOnCourse(courseSlug, returnObject = false) {
      if (process.server)
        return returnObject ? { hasAccess: false, accessType: null } : false;
      if (!courseSlug || typeof courseSlug !== "string")
        return returnObject ? { hasAccess: false, accessType: null } : false;

      const authStore = useAuthStore();
      if (!authStore.isLoggedIn)
        return returnObject ? { hasAccess: false, accessType: null } : false;

      try {
        const res = await $fetch(
          `/api/user/has-access/${encodeURIComponent(courseSlug)}`,
          { credentials: "include" }
        );
        const hasAccess = res.success === true && Boolean(res.hasAccess);
        const accessType = res.accessType || null;

        return returnObject ? { hasAccess, accessType } : hasAccess;
      } catch (err) {
        return returnObject ? { hasAccess: false, accessType: null } : false;
      }
    },
*/

    getCourseProgress(courseIdOrSlug) {
      if (!this.progress.length) return { success: false, data: null };
      const entry = this.progress.find(
        (p) => p.course_id === courseIdOrSlug || p.slug === courseIdOrSlug
      );
      return entry
        ? { success: true, data: entry }
        : { success: false, data: null };
    },
    // === Progression (tableau) ===
    async fetchProgress() {
      try {
        // L’API renvoie { success, data: [...] }
        const res = await $fetch("/api/user/progress", {
          credentials: "include",
        });
        if (res?.success === true && Array.isArray(res.data)) {
          this.progress = res.data;
          // console.log("PROGRESSION ATTENDUE DANS USERSTORE", this.progress);
        } else {
          console.error("fetchProgress response invalide:", res);
        }
      } catch (err) {
        console.error("fetchProgress error:", err);
      }
    },

    // === Adresse (objet, pas tableau) ===
    async fetchAddress() {
      try {
        const res = await $fetch("/api/user/address", {
          credentials: "include",
        });
        // Attention : la réponse attendue est { address: { … } }
        if (res?.address) {
          this.address = res.address;
        }
      } catch (err) {
        console.error("fetchAddress error:", err);
      }
    },

    // === Factures (tableau) ===
    async fetchInvoices() {
      try {
        const res = await $fetch("/api/user/invoices", {
          credentials: "include",
        });
        // On vérifie que invoices est bien un tableau
        if (Array.isArray(res?.invoices)) {
          this.invoices = res.invoices;
        }
      } catch (err) {
        console.error("fetchInvoices error:", err);
      }
    },

    /**
     * Initialise en une fois toutes les données utilisateur
     */

    // === Initialisation ===
    async initializeUserData() {
      const auth = useAuthStore();
      if (!auth.isLoggedIn) return;

      // 1) Tout ce qui est tableaux (achats, contenu, notifs, favoris)
      await Promise.all([
        this.fetchAllPurchases(),
        this.fetchAllMyContent(),
        this.fetchNotifications(),
        this.fetchInstallmentCourses(), // ← ICI
        this.fetchSubscribedCourses(), // ← ICI

        await this.fetchQuizAttempts(),

        Promise.all(PURCHASE_CATEGORIES.map((cat) => this.fetchFavorites(cat))),
      ]);

      // 2) Ensuite, adresse, factures et progression
      await Promise.all([
        this.fetchAddress(), // <— nouvel appel
        this.fetchInvoices(),
        this.fetchProgress(), // <— nouvel appel
      ]);
    },
    // Désactivation du compte utilisateur (soft delete)
    async deactivateAccount() {
      try {
        const response = await $fetch("/api/user/profile/deactivate", {
          method: "POST",
          credentials: "include",
        });
        if (response?.success) {
          // On peut faire un logout direct ici ou côté composant
          const authStore = useAuthStore();
          authStore.logout();
        }
        return response;
      } catch (err) {
        return {
          success: false,
          message: err.message || "Erreur lors de la désactivation",
        };
      }
    },

    // Réactivation du compte utilisateur
    async reactivateAccount({ email, password }) {
      try {
        const response = await $fetch("/api/user/profile/reactivate", {
          method: "POST",
          body: { email, password },
          credentials: "include",
        });
        return response;
      } catch (err) {
        return {
          success: false,
          message: err.message || "Erreur lors de la réactivation",
        };
      }
    },

    async deleteAccount() {
      try {
        const response = await $fetch("/api/user/profile/delete", {
          method: "POST",
          credentials: "include",
        });
        if (response?.success) {
          // logout immédiat
          const authStore = useAuthStore();
          authStore.logout();
        }
        return response;
      } catch (err) {
        return {
          success: false,
          message: err.message || "Erreur lors de la suppression définitive",
        };
      }
    },
    // Met à jour le profil utilisateur (hors mot de passe)
    async updateProfile(formData) {
      try {
        const response = await $fetch("/api/user/profile/edit", {
          method: "POST",
          body: formData, // FormData pour gérer l’avatar
          credentials: "include",
        });
        if (response?.success && response.profile) {
          // Mets à jour le store local (optionnel mais conseillé)
          this.profile = { ...this.profile, ...response.profile };
        }
        return response;
      } catch (err) {
        return {
          success: false,
          message: err.message || "Erreur lors de la mise à jour du profil.",
        };
      }
    },
  },
});
